import { buildKey, usePersistFilterStorageKey, useSafeLocalStorage } from '@/_shared/tools/storage.js';
import {
    Button,
    Divider,
    Menu,
    type MenuCheckedValueChangeData,
    type MenuCheckedValueChangeEvent,
    MenuGroup,
    MenuGroupHeader,
    MenuItem,
    MenuItemCheckbox,
    MenuList,
    MenuPopover,
    MenuTrigger,
} from '@fluentui/react-components';
import { EyeRegular } from '@fluentui/react-icons';
import type { Table } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo } from 'react';
import { useStyles } from './styles.js';

type StoredFilterSchema = {
    id: string;
    value: unknown;
};

type DataTableToolbarViewOptionsProps<TData> = {
    tableId: string;
    table: Table<TData>;
    resetTableLayout: () => void;
    columnTitles: Partial<Record<keyof TData, string>>;
    columnOrder: string[];
};

export function DataTableToolbarViewOptions<TData>({
    tableId,
    table,
    resetTableLayout,
    columnTitles,
    columnOrder,
}: DataTableToolbarViewOptionsProps<TData>) {
    const styles = useStyles();

    const sortedColumns = useMemo(() => {
        return table.getAllColumns().sort((a, b) => {
            return columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id);
        });
    }, [columnOrder, table]);

    const handleChange = useCallback(
        (_event: MenuCheckedValueChangeEvent, { name, checkedItems }: MenuCheckedValueChangeData) => {
            if (name === 'column') {
                sortedColumns.forEach((column) => {
                    column.toggleVisibility(checkedItems.includes(column.id));
                });
            }
        },
        [sortedColumns]
    );

    const checkedValues = {
        column: sortedColumns
            .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
            .map((column) => (column.getIsVisible() ? column.id : undefined))
            .filter(Boolean) as string[],
    };

    // filters
    const persistLocalStorageFilterKey = usePersistFilterStorageKey({
        id: tableId,
    });

    const [storedFilters, setStoredFilters] = useSafeLocalStorage<StoredFilterSchema[]>(
        persistLocalStorageFilterKey,
        () => []
    );

    // load the stored filters on initial load
    useEffect(() => {
        if (!storedFilters.length) {
            return;
        }

        storedFilters.forEach((filter) => {
            const column = table.getColumn(filter.id);

            if (column && filter.value !== undefined) {
                column.setFilterValue(filter.value);
            }
        });
    }, []);

    const onSave = useCallback(() => {
        setStoredFilters(
            table.getAllColumns().map((column) => {
                return {
                    id: column.id,
                    value: column.getFilterValue(),
                };
            })
        );
    }, [table, setStoredFilters]);

    const onDelete = useCallback(() => {
        // clear filters
        setStoredFilters([]);
        table.setColumnFilters([]);

        const localStorageFilterPrefix = buildKey([tableId, 'filter']);

        // clear localStorage
        Object.keys(localStorage).forEach((key) => {
            if (key.startsWith(localStorageFilterPrefix)) {
                localStorage.removeItem(key);
            }
        });

        // clear sessionStorage
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith(localStorageFilterPrefix)) {
                sessionStorage.removeItem(key);
            }
        });
    }, [table, tableId, setStoredFilters]);

    return (
        <Menu positioning={{ autoSize: true }} checkedValues={checkedValues} onCheckedValueChange={handleChange}>
            <MenuTrigger disableButtonEnhancement>
                <Button className={styles.button} icon={<EyeRegular />} size="small">
                    Ansicht
                </Button>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    <MenuGroup>
                        <MenuGroupHeader>Spalten</MenuGroupHeader>

                        {sortedColumns
                            .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
                            .map((column) => {
                                return (
                                    <MenuItemCheckbox key={column.id} name="column" value={column.id}>
                                        {columnTitles[column.id as keyof TData]}
                                    </MenuItemCheckbox>
                                );
                            })}
                    </MenuGroup>

                    <Divider className={styles.divider} />

                    <MenuGroup>
                        <MenuGroupHeader>Layout</MenuGroupHeader>

                        <MenuItem onClick={resetTableLayout}>
                            <span>Layout zurücksetzen</span>
                        </MenuItem>
                    </MenuGroup>

                    <Divider className={styles.divider} />

                    <MenuGroup>
                        <MenuGroupHeader>Standardfilter</MenuGroupHeader>

                        <MenuItem onClick={onSave}>
                            <span>Standardfilter speichern</span>
                        </MenuItem>

                        <MenuItem onClick={onDelete}>
                            <span>Standardfilter löschen</span>
                        </MenuItem>
                    </MenuGroup>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
}
